<template>
    <div
        :class="{
            'notifications-wrapper': true,
            'is-active': isActive,
        }"
    >
        <button
            type="button"
            class="notification-btn"
            @click.prevent.stop="toggleActive"
            aria-label="Your Notifications"
        >
            <SvgIcon
                icon="filled-notifications"
                classes="notification-btn__icon"
                :is-filled="true"
            />
            <span
                v-if="count > 0"
                v-text="`+${count}`"
                class="notification-btn__count"
            />
        </button>
        <section class="notifications">
            <header class="notifications__header">
                <h3>Notifications</h3>
                <a href="#" v-show="notifications && notifications.length > 0" @click.prevent.stop="handleClearAll">
                    Clear All
                </a>
            </header>
            <div v-if="$wait.is('get_notifications')" class="loading">
                <Spinner :height="40" :width="40"/>
            </div>
            <template v-else-if="notifications && notifications.length > 0">
                <ul class="notifications-list">
                    <li v-for="notification in notifications" :class="{'is-read': notification.read}">
                        <button class="notification-detail" @click.prevent.stop="handleNotificationClicked(notification)">
                            <span class="notification-detail__message" v-html="notification.message"/>
                            <div class="notification-detail__avatars">
                                <img
                                    v-for="user in notification.related_users.slice(0, 3)"
                                    :src="user.avatar ? user.avatar.url : '//placehold.it/32x32'"
                                    :alt="`Profile Image for ${user.name}`"
                                />
                            </div>
                            <span class="notification-detail__date" v-text="notification.date_formatted"/>
                        </button>
                        <button class="clear-notification" type="button" @click.prevent.stop="handleClear(notification.id)">
                            ✕
                        </button>
                    </li>
                </ul>
                <router-link :to="{ name: 'view-notifications' }" class="notifications__all">
                    View All
                </router-link>
            </template>
            <div v-else class="no-results">
                <span>You're all caught up!</span>
            </div>
        </section>
    </div>
</template>
<script>
import debounce from 'lodash.debounce';
import unionBy from 'lodash.unionby';
import { mapState } from 'vuex';
import API from '@api';
import SvgIcon from '@components/SvgIcon';
import Spinner from '@components/Spinner';

export default {
    name: 'Notifications',
    components: {
        Spinner,
        SvgIcon,
    },
    props: {
        count: {
            type: Number,
            default: 0,
        }
    },
    data: function() {
        return {
            isActive: false,
            notifications: null,
            clearCb: null,
        };
    },
    methods: {
        getNotifications: async function(visible = true) {
            try {
                if(visible) {
                    this.$wait.start('get_notifications');
                }
                const data = await API.getNotifications();
                this.notifications = unionBy([], data, this.items, 'id');
            } catch(err) {
                console.warn('get_notifications');
            } finally {
                this.$wait.end('get_notifications');
            }
        },
        handleClear: async function(notificationId) {
            try {
                this.notifications = this.notifications.filter((notif) => notif.id !== notificationId);
                await API.clearNotification(notificationId);
            } catch(err) {
                console.warn('Failed to clear notification');
            } finally {
                this.getNotifications(false);
                this.$emit('updated');
            }
        },
        handleClearAll: async function() {
            try {
                this.notifications = null;
                await API.clearAllNotifications();
            } catch(err) {
                console.warn('Failed to clear notification');
            } finally {
                this.getNotifications(false);
                this.$emit('updated');
            }
        },
        handleReadAll: async function() {
            const unread = this.notifications.filter((notif) => !notif.read);

            if(unread.length > 0) {
                await API.readAllNotifications();
                this.getNotifications(false);
                this.$emit('updated');
            }
        },
        handleNotificationClicked: function(notification){
            API.readNotification(notification.id);
            this.$router.push(notification.url);
        },
        toggleActive: function() {
            this.$emit('updated');

            if(!this.isActive) {
                if(!this.$wait.is('get_notifications')) {
                    this.getNotifications();
                }

                this.clearCb();
                this.isActive = true;
            } else {
                this.isActive = false;

                if(this.clearCb) {
                    this.clearCb.cancel();
                }
            }
        },
    },
    mounted: function() {
        this.clearCb = debounce(this.handleReadAll, 8000);
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
        }),
    },
}
</script>
<style lang="scss">
@import '~@/theme/_variables.scss';

span.highlight {
    color: $link;
}
</style>
<style lang="scss" scoped>
@import '~@/theme/_variables.scss';

.no-results {
    padding: 1.5rem;
    text-align: center;
}

.notification-btn {
    appearance: none;
    -webkit-appearance: none;
    background-color: $accent;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3.6rem;
    width: 3.6rem;
    border-radius: 50%;
    border: none;
    position: relative;
    cursor: pointer;
    z-index: 10;

    &:hover {
        background-color: #0E716D;
    }

    .notification-btn__icon {
        height: 1.6rem;
        width: 1.6rem;
        margin-right: 0;
        color: white;
    }

    .notification-btn__count {
        position: absolute;
        top: -.4rem;
        left: calc(50% + .4rem);
        background-color: $accent-header;
        color: white;
        font-family: Axiforma, sans-serif;
        font-size: 1rem;
        line-height: 1.2rem;
        border-radius: .6rem;
        padding: 0.5rem 0.5rem 0.4rem;
    }
}


.notifications {
    flex-direction: column;
    border-radius: .6rem;
    max-width: calc(100vw - 4rem);
    color: black;
    display: none;
    background-color: white;
    box-shadow: 0 .6rem 2rem 0 rgba(49, 49, 74, 0.16);
    position: absolute;
    top: -.5rem;
    right: -.5rem;
    z-index: 9;
    width: 61rem;

    .notifications__header {
        padding: 1.6rem 6rem 1.4rem 1.5rem;
        display: flex;
        align-items: center;
        border-bottom: .1rem solid $border;

        h3 {
            margin-bottom: 0;
        }

        a {
            margin-left: auto;
            margin-right: 0;
        }
    }

    .notifications__all {
        font-weight: 600;
        border-top: .1rem solid $border;
        text-align: center;
        font-size: 1.4rem;
        line-height: 1.4rem;
        padding: 1.4rem 2rem;
    }
}

.notification-detail {
    .notification-detail__message {
        max-width: 41.5rem;
    }
}


.notifications-wrapper {
    position: relative;

    &.is-active {
        z-index: 50;

        .notifications {
            display: flex;
        }
    }
}

</style>
